import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  CircularProgress,
  Typography,
  InputAdornment,
} from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  searchField: {
    marginBottom: theme.spacing(1),
  },
  list: {
    maxHeight: 400,
    overflow: "auto",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  emptyText: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  avatar: {
    width: 36,
    height: 36,
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    display: "inline-block",
    marginRight: 6,
  },
  offlineDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#BDBDBD",
    display: "inline-block",
    marginRight: 6,
  },
}));

const UserSelectionDialog = ({ open, onClose, onSelect, title = "Selecionar Vendedor" }) => {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/users/list");
        if (!cancelled) setUsers(data || []);
      } catch (err) {
        console.error("[UserSelectionDialog] error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = search.trim()
    ? users.filter((u) =>
        (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const handleSelect = useCallback(
    (userId) => {
      onSelect(userId);
    },
    [onSelect]
  );

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          className={classes.searchField}
          placeholder="Buscar vendedor..."
          fullWidth
          size="small"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <div className={classes.loadingCenter}>
            <CircularProgress size={24} />
          </div>
        ) : filtered.length === 0 ? (
          <Typography className={classes.emptyText}>
            Nenhum vendedor encontrado
          </Typography>
        ) : (
          <List className={classes.list} dense>
            {filtered.map((user) => (
              <ListItem
                key={user.id}
                button
                onClick={() => handleSelect(user.id)}
              >
                <ListItemAvatar>
                  <Avatar
                    className={classes.avatar}
                    src={user.profileImage || undefined}
                  >
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <>
                      <span
                        className={
                          user.online ? classes.onlineDot : classes.offlineDot
                        }
                      />
                      {user.name}
                    </>
                  }
                  secondary={user.email}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(UserSelectionDialog);
